import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, TrendingUp, TrendingDown, Minus, Brain, Loader2, 
  ArrowLeft, BarChart3, Target, DollarSign, AlertTriangle,
  ChevronDown, ChevronUp, RefreshCw, Zap, Shield, Flame,
  Clock, Activity, Eye, CheckCircle, LineChart, PieChart,
  ArrowUpRight, ArrowDownRight, Scale, Percent, Calendar,
  Maximize2, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { normalizeSymbol } from '../utils/symbolUtils';
import { usePayoffDiagram } from '../contexts/PayoffDiagramContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TradingView Widget Component
const TradingViewWidget = ({ symbol, interval = "D", height = "400px" }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!symbol || !containerRef.current) return;
    
    containerRef.current.innerHTML = '';
    
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      "autosize": true,
      "symbol": symbol,
      "interval": interval,
      "timezone": "America/New_York",
      "theme": "dark",
      "style": "1",
      "locale": "en",
      "enable_publishing": false,
      "backgroundColor": "rgba(15, 23, 42, 1)",
      "gridColor": "rgba(51, 65, 85, 0.3)",
      "hide_top_toolbar": false,
      "hide_legend": false,
      "save_image": true,
      "calendar": false,
      "hide_volume": false,
      "support_host": "https://www.tradingview.com",
      "studies": ["RSI@tv-basicstudies", "MASimple@tv-basicstudies"]
    });
    
    containerRef.current.appendChild(script);
    
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [symbol, interval]);
  
  return (
    <div className="tradingview-widget-container w-full rounded-lg overflow-hidden" style={{ height }} ref={containerRef}>
      <div className="tradingview-widget-container__widget h-full w-full"></div>
    </div>
  );
};

// Fullscreen Chart Modal
const FullscreenChart = ({ symbol, interval, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 flex flex-col" data-testid="fullscreen-chart-overlay">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-5 w-5 text-indigo-400" />
          <span className="text-white font-semibold text-lg">{symbol} - Live Chart</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"
            onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${symbol}`, '_blank')}>
            <Eye className="h-4 w-4 mr-1" /> Open in TradingView
          </Button>
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-700"
            onClick={onClose} data-testid="close-fullscreen-chart-btn">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
      <div className="flex-1 p-2">
        <TradingViewWidget symbol={symbol} interval={interval} height="100%" />
      </div>
      <div className="px-4 py-1.5 bg-slate-900 border-t border-slate-800 text-center">
        <span className="text-xs text-slate-500">Press <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-400 text-xs">Esc</kbd> to close</span>
      </div>
    </div>
  );
};

// Strategy Card Component
const StrategyCard = ({ strategy, currentPrice, symbol, onViewPayoff }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getTypeColor = (type) => {
    switch (type) {
      case 'bullish': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'bearish': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'neutral': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'income': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'volatility': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };
  
  const getTypeIcon = (type) => {
    switch (type) {
      case 'bullish': return <TrendingUp className="w-4 h-4" />;
      case 'bearish': return <TrendingDown className="w-4 h-4" />;
      case 'neutral': return <Minus className="w-4 h-4" />;
      case 'income': return <DollarSign className="w-4 h-4" />;
      case 'volatility': return <Activity className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };
  
  const getProbColor = (prob) => {
    if (prob >= 60) return 'text-emerald-400';
    if (prob >= 40) return 'text-amber-400';
    return 'text-red-400';
  };
  
  return (
    <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`${getTypeColor(strategy.strategy_type)} border`}>
              {getTypeIcon(strategy.strategy_type)}
              <span className="ml-1 capitalize">{strategy.strategy_type}</span>
            </Badge>
            <h3 className="text-lg font-semibold text-white">{strategy.strategy_name}</h3>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold ${getProbColor(strategy.probability_of_profit)}`}>
              {strategy.probability_of_profit}%
            </div>
            <div className="text-xs text-slate-500">Win Rate</div>
          </div>
        </div>
        <p className="text-sm text-slate-400 mt-1">{strategy.description}</p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-2 bg-slate-900/50 rounded-lg">
            <div className="text-xs text-slate-500">Max Profit</div>
            <div className="text-emerald-400 font-semibold">{strategy.max_profit}</div>
          </div>
          <div className="p-2 bg-slate-900/50 rounded-lg">
            <div className="text-xs text-slate-500">Max Loss</div>
            <div className="text-red-400 font-semibold">{strategy.max_loss}</div>
          </div>
          <div className="p-2 bg-slate-900/50 rounded-lg">
            <div className="text-xs text-slate-500">Break Even</div>
            <div className="text-white font-semibold">
              {strategy.break_even.map(be => `$${be}`).join(', ')}
            </div>
          </div>
          <div className="p-2 bg-slate-900/50 rounded-lg">
            <div className="text-xs text-slate-500">Capital Req.</div>
            <div className="text-indigo-400 font-semibold">${strategy.required_capital}</div>
          </div>
        </div>
        
        {/* Legs Summary */}
        <div className="flex flex-wrap gap-2">
          {strategy.legs.map((leg, idx) => (
            <Badge 
              key={idx} 
              variant="outline" 
              className={`${leg.position_type === 'long' ? 'border-emerald-500/50 text-emerald-400' : 'border-red-500/50 text-red-400'}`}
            >
              {leg.position_type === 'long' ? 'Buy' : 'Sell'} ${leg.strike} {leg.option_type.toUpperCase()} @ ${leg.premium}
            </Badge>
          ))}
          <Badge variant="outline" className="border-slate-600 text-slate-400">
            <Calendar className="w-3 h-3 mr-1" />
            {strategy.legs[0]?.expiration}
          </Badge>
        </div>
        
        {/* Expandable Details */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="w-full text-slate-400 hover:text-white"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Hide Details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Show Details
            </>
          )}
        </Button>
        
        {expanded && (
          <div className="space-y-4 pt-2 border-t border-slate-700">
            {/* Detailed Legs */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Position Details</h4>
              <div className="space-y-2">
                {strategy.legs.map((leg, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        leg.position_type === 'long' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {leg.position_type === 'long' ? 'BUY' : 'SELL'}
                      </span>
                      <span className="text-white">{leg.quantity}x</span>
                      <span className="text-slate-300">
                        ${leg.strike} {leg.option_type.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-medium">${leg.premium}</div>
                      <div className="text-xs text-slate-500">Exp: {leg.expiration}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Risk/Reward Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Scale className="w-4 h-4 text-indigo-400" />
                  <span className="text-sm text-slate-400">Risk/Reward</span>
                </div>
                <div className="text-white font-semibold">{strategy.risk_reward_ratio}</div>
              </div>
              <div className="p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-slate-400">Days to Exp</span>
                </div>
                <div className="text-white font-semibold">{strategy.days_to_expiration} days</div>
              </div>
            </div>
            
            {/* Best If & Warnings */}
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-emerald-400">Best If</div>
                  <div className="text-sm text-slate-300">{strategy.best_if}</div>
                </div>
              </div>
            </div>
            
            {strategy.warnings && strategy.warnings.length > 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-amber-400">Warnings</div>
                    <ul className="text-sm text-slate-300 list-disc list-inside">
                      {strategy.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Greeks */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Greeks</h4>
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-xs text-slate-500">Delta</div>
                  <div className={`font-semibold ${strategy.greeks.delta > 0 ? 'text-emerald-400' : strategy.greeks.delta < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                    {strategy.greeks.delta > 0 ? '+' : ''}{strategy.greeks.delta}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-slate-500">Theta</div>
                  <div className={`font-semibold ${strategy.greeks.theta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {strategy.greeks.theta > 0 ? '+' : ''}{strategy.greeks.theta}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-slate-500">Vega</div>
                  <div className={`font-semibold ${strategy.greeks.vega > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {strategy.greeks.vega > 0 ? '+' : ''}{strategy.greeks.vega}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => onViewPayoff(strategy)}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
          >
            <PieChart className="w-4 h-4 mr-2" />
            View P&L Chart
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const StockStrategyAnalyzer = () => {
  const [searchSymbol, setSearchSymbol] = useState('');
  const [symbol, setSymbol] = useState('');
  const [aiModel, setAiModel] = useState('gemini');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showChart, setShowChart] = useState(true);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  
  const { openPayoffDiagram } = usePayoffDiagram();
  
  const analyzeStock = async () => {
    const normalized = normalizeSymbol(searchSymbol);
    if (!normalized) {
      toast.error('Please enter a valid stock symbol');
      return;
    }
    
    setSymbol(normalized);
    setLoading(true);
    setResult(null);
    toast.info('Analyzing strategies... This may take 15-30 seconds');
    
    try {
      const response = await axios.post(`${API}/stock-analyzer/analyze`, {
        symbol: normalized,
        ai_model: aiModel
      }, { timeout: 120000 });
      
      if (response.data.success) {
        setResult(response.data);
        toast.success(`Found ${response.data.strategies.length} strategies for ${normalized}`);
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze stock');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewPayoff = (strategy) => {
    if (!result) return;
    
    // Format position for payoff diagram
    const position = {
      underlying_symbol: result.symbol,
      strategy_name: strategy.strategy_name,
      is_spread: strategy.legs.length > 1,
      legs: strategy.legs.map(leg => ({
        option_type: leg.option_type,
        strike_price: leg.strike,
        quantity: leg.position_type === 'long' ? leg.quantity : -leg.quantity,
        premium: leg.premium,
        expiration_date: leg.expiration
      }))
    };
    
    openPayoffDiagram(position, result.current_price);
  };
  
  const filteredStrategies = result?.strategies?.filter(s => {
    if (activeFilter === 'all') return true;
    return s.strategy_type === activeFilter;
  }) || [];
  
  const getTrendIcon = (trend) => {
    if (trend === 'BULLISH') return <TrendingUp className="w-5 h-5 text-emerald-400" />;
    if (trend === 'BEARISH') return <TrendingDown className="w-5 h-5 text-red-400" />;
    return <Minus className="w-5 h-5 text-slate-400" />;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-900/80 border-b border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-700" />
              <div className="flex items-center gap-2">
                <Target className="w-6 h-6 text-indigo-400" />
                <h1 className="text-xl font-bold text-white">Stock Strategy Analyzer</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Label className="text-slate-400 text-sm">AI Model:</Label>
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="gemini" className="text-white">Gemini 2.5 Pro</SelectItem>
                  <SelectItem value="claude" className="text-white">Claude Sonnet 4.5</SelectItem>
                  <SelectItem value="fast" className="text-white">Gemini Flash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Search Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[250px] space-y-2">
                <Label className="text-slate-300">Stock Symbol</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter symbol (e.g., AAPL, NVDA, TSLA)"
                    value={searchSymbol}
                    onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && analyzeStock()}
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                    data-testid="stock-symbol-input"
                  />
                  <Button
                    onClick={analyzeStock}
                    disabled={loading}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 min-w-[150px]"
                    data-testid="analyze-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Analyze
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Results */}
        {result && (
          <>
            {/* Stock Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Market Data Card */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Activity className="w-5 h-5 text-indigo-400" />
                    {result.symbol} - {result.stock_name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold text-white">${result.current_price}</span>
                    <div className="flex items-center gap-2">
                      {getTrendIcon(result.market_data.trend)}
                      <Badge className={`${
                        result.market_data.trend === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' :
                        result.market_data.trend === 'BEARISH' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {result.market_data.trend}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-slate-500">52W High</div>
                      <div className="text-white font-medium">${result.market_data.high_52w}</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-slate-500">52W Low</div>
                      <div className="text-white font-medium">${result.market_data.low_52w}</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-slate-500">SMA 20</div>
                      <div className="text-white font-medium">${result.market_data.sma_20}</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-slate-500">SMA 50</div>
                      <div className="text-white font-medium">${result.market_data.sma_50}</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-slate-500">Hist. Vol.</div>
                      <div className="text-amber-400 font-medium">{result.market_data.volatility}%</div>
                    </div>
                    <div className="p-2 bg-slate-900/50 rounded">
                      <div className="text-slate-500">Impl. Vol.</div>
                      <div className="text-amber-400 font-medium">{result.market_data.implied_volatility}%</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* AI Analysis Card */}
              <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Brain className="w-5 h-5 text-purple-400" />
                    AI Market Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-invert prose-sm max-w-none text-slate-300">
                    <ReactMarkdown
                      components={{
                        p: ({node, ...props}) => <p className="text-slate-300 my-2 leading-relaxed" {...props} />,
                        strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                      }}
                    >
                      {result.ai_analysis}
                    </ReactMarkdown>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Chart Section */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <LineChart className="w-5 h-5 text-indigo-400" />
                    {result.symbol} - Live Chart
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {showChart && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                        onClick={() => setChartFullscreen(true)}
                        data-testid="expand-chart-btn"
                      >
                        <Maximize2 className="h-4 w-4 mr-1.5" />
                        Expand
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowChart(!showChart)}
                      className="text-slate-400"
                    >
                      {showChart ? 'Hide Chart' : 'Show Chart'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {showChart && (
                <CardContent className="p-0">
                  <TradingViewWidget symbol={result.symbol} interval="D" />
                </CardContent>
              )}
            </Card>

            {/* Fullscreen Chart Modal */}
            {chartFullscreen && result?.symbol && (
              <FullscreenChart
                symbol={result.symbol}
                interval="D"
                onClose={() => setChartFullscreen(false)}
              />
            )}
            
            {/* Strategy Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-400 text-sm">Filter:</span>
              {['all', 'bullish', 'bearish', 'neutral', 'income', 'volatility'].map(filter => (
                <Button
                  key={filter}
                  variant={activeFilter === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveFilter(filter)}
                  className={activeFilter === filter 
                    ? 'bg-indigo-600' 
                    : 'border-slate-700 text-slate-400 hover:text-white'
                  }
                >
                  {filter === 'all' ? 'All Strategies' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Button>
              ))}
            </div>
            
            {/* Strategies Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredStrategies.map((strategy, idx) => (
                <StrategyCard
                  key={idx}
                  strategy={strategy}
                  currentPrice={result.current_price}
                  symbol={result.symbol}
                  onViewPayoff={handleViewPayoff}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Empty State */}
        {!result && !loading && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <Target className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Enter a Stock Symbol</h2>
              <p className="text-slate-400 max-w-md mx-auto">
                Get AI-powered option strategy recommendations with specific strikes, 
                expirations, probability of success, and P&L charts.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {['AAPL', 'NVDA', 'TSLA', 'SPY', 'MSFT'].map(sym => (
                  <Button
                    key={sym}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchSymbol(sym);
                      setSymbol(sym);
                    }}
                    className="border-slate-700 text-slate-400 hover:text-white"
                  >
                    {sym}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Loading State */}
        {loading && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <Loader2 className="w-16 h-16 mx-auto text-indigo-400 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Analyzing {symbol}...</h2>
              <p className="text-slate-400">
                Fetching market data, calculating probabilities, and generating AI recommendations
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default StockStrategyAnalyzer;
